class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.boundClose = this.close.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.boundClose);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // Skip if already open — prevents duplicate rAFs and transitionend listeners
    if (this.isOpen) return;
    this.isOpen = true;
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Use rAF to ensure the browser has painted before triggering the animation
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.isOpen) return;
        this.classList.add('animate', 'active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        // Only trap focus if drawer is still open — prevents stale listener from firing on close transition
        if (!this.isOpen) return;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.isOpen = false;
    this.classList.remove('active', 'animate');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      // Preserve checkout + express payment buttons for cart-drawer section
      if (section.id === 'cart-drawer' && typeof window.updateDrawerPreservingButtons === 'function' && parsedState.sections[section.id]) {
        window.updateDrawerPreservingButtons(parsedState.sections[section.id]);
      } else {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });
    if (typeof window.suppressProgressBarAnimation === 'function') window.suppressProgressBarAnimation();
    if (typeof window.initUpsellDots === 'function') window.initUpsellDots();

    this.rebindOverlay();
    // Reset isOpen so open() can proceed (it early-returns if already open)
    this.isOpen = false;
    this.open();
  }

  updateContents(parsedState) {
    if (!this.isOpen) return;
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      if (section.id === 'cart-drawer' && typeof window.updateDrawerPreservingButtons === 'function' && parsedState.sections[section.id]) {
        window.updateDrawerPreservingButtons(parsedState.sections[section.id]);
      } else {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (sectionElement && parsedState.sections[section.id]) {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });
    if (typeof window.suppressProgressBarAnimation === 'function') window.suppressProgressBarAnimation();
    if (typeof window.initUpsellDots === 'function') window.initUpsellDots();
    this.rebindOverlay();
  }

  rebindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.removeEventListener('click', this.boundClose);
      overlay.addEventListener('click', this.boundClose);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items' || event.source === 'product-form') {
        return;
      }
      this.onCartUpdate();
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);
    if (typeof window._updateCheckoutBusy === 'function') window._updateCheckoutBusy();

    const sections = this.getSectionsToRender();
    const body = JSON.stringify({
      line,
      quantity,
      sections: sections.map((s) => s.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          const quantityElement = document.getElementById(`Drawer-quantity-${line}`);
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        sections.forEach((section) => {
          // Preserve checkout + express payment buttons for cart-drawer section
          if (section.section === 'cart-drawer' && typeof window.updateDrawerPreservingButtons === 'function' && parsedState.sections[section.section]) {
            window.updateDrawerPreservingButtons(parsedState.sections[section.section]);
          } else {
            const parent = document.getElementById(section.id);
            if (!parent) return;
            const elementToReplace = parent.querySelector(section.selector) || parent;
            if (parsedState.sections[section.section]) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            }
          }
        });
        if (typeof window.suppressProgressBarAnimation === 'function') window.suppressProgressBarAnimation();
        // Sync progress bar with fresh cart data (initTieredProgress was called inside
        // updateDrawerPreservingButtons, but _lastKnownCart may be stale at that point
        // due to the async fetch interceptor race)
        window._lastKnownCart = parsedState;
        var progressEl = document.querySelector('[data-tiered-progress]');
        if (progressEl && progressEl._tieredInstance) {
          progressEl._tieredInstance.update(parsedState.total_price, parsedState);
        }
        if (typeof window.initUpsellDots === 'function') window.initUpsellDots();

        // Re-bind overlay click after DOM replacement
        const overlay = document.getElementById('CartDrawer-Overlay');
        if (overlay && cartDrawerWrapper && cartDrawerWrapper.boundClose) {
          overlay.removeEventListener('click', cartDrawerWrapper.boundClose);
          overlay.addEventListener('click', cartDrawerWrapper.boundClose);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
        if (typeof window._updateCheckoutBusy === 'function') window._updateCheckoutBusy();
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
